import React, { useContext, useState } from "react";
import { MenuProps } from "antd";
import {
	SidebarImage,
	SidebarMenu,
	SidebarContainer,
} from "./styles.";
import {
	ReadFilled,
	StarFilled,
	SnippetsFilled,
} from "@ant-design/icons";
import { NavLink, useLocation } from "react-router-dom";
import { SidebarContext } from "../../context/SidebarMenuProvider";

type MenuItem = {
	key: React.Key;
	icon?: React.ReactNode;
	label: React.ReactNode;
	type?: "group";
} & ({ children?: MenuItem[] } | { type: "divider" });

function getItem(
	label: React.ReactNode,
	key: React.Key,
	icon?: React.ReactNode,
	children?: MenuItem[],
	type?: "group"
): MenuItem {
	return {
		key,
		icon,
		children,
		label,
		type,
	} as MenuItem;
}

interface ChildMenuItem {
	name: string;
	url: string;
	displayText: string;
	isDisabled: boolean;
}

interface ParentMenuItem {
	name: string;
	url: string;
	displayText: string;
	id: number;
	children?: ChildMenuItem[];
	icon: string;
	isDisabled: boolean;
}

const Sidebar: React.FC = () => {
	const [openKeys, setOpenKeys] = useState<string[]>([]);
	const { sidebarData, isSidebarOpen } = useContext(SidebarContext);
	const location = useLocation();
	const currentPath = location.pathname;

	const sidebarItems = sidebarData
		.map((item: ParentMenuItem) => {
			const isActive = currentPath === `/${item.url}`; 

			if (item.children?.length === 0) {
				return getItem(
					<NavLink
						className={`${item.isDisabled ? "disabled-header" : "nav-header"} ${isActive ? "active" : ""}`}
						to={`/${item.url}`}
					>
						{item.displayText}
					</NavLink>,
					String(item.id),
					<SidebarImage className={isActive ? "active" : ""}>
						{item.id === 1 ? <ReadFilled /> : <StarFilled />}
					</SidebarImage>
				);
			} else {
				return getItem(
					item.displayText,
					String(item.id),
					<SidebarImage className={isActive ? "active" : ""}>
						<SnippetsFilled />
					</SidebarImage>,
					item.children?.map((child: ChildMenuItem, index: number) => {
						return getItem(
							<NavLink
								className={`${child.isDisabled ? "disabled-link" : "nav-link"} ${isActive ? "active" : ""}`}
								to={`/${child.url}`}
							>
								{child.displayText}
							</NavLink>,
							`${item.id}-${index}`
						);
					})
				);
			}
		})
		.filter(Boolean);

	const onOpenChange: MenuProps["onOpenChange"] = (keys) => {
		const latestOpenKey = keys.find((key) => !openKeys.includes(key));
		if (latestOpenKey) {
			setOpenKeys([latestOpenKey]);
		} else if (keys.length < openKeys.length) {
			setOpenKeys(keys);
		}
	};

	const onClick: MenuProps["onClick"] = (e) => {
		if (e.keyPath.length <= 1) {
			setOpenKeys([]);
		}
	};

	return (
		<SidebarContainer collapsed={isSidebarOpen}>
			{sidebarData.length > 0 && (
				<SidebarMenu
					onClick={onClick}
					mode="inline"
					items={sidebarItems as any}
					openKeys={openKeys}
					onOpenChange={onOpenChange}
					inlineCollapsed={isSidebarOpen}
				/>
			)}
		</SidebarContainer>
	);
};

export default Sidebar;
